import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import {
  getBillingSummary,
  listPayments,
  type BillingSummary,
  type PaymentItem,
  type PaymentStatusUpper,
} from '@/lib/operatorApi';
import { formatKop, formatDateTime } from '@/lib/format';

const STATUS_FILTERS: { value: PaymentStatusUpper | 'all'; label: string }[] = [
  { value: 'all',       label: 'Все' },
  { value: 'SUCCEEDED', label: 'Успешные' },
  { value: 'FAILED',    label: 'Ошибки' },
  { value: 'REFUNDED',  label: 'Возвраты' },
  { value: 'PENDING',   label: 'В ожидании' },
];

export default function Billing() {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<PaymentStatusUpper | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getBillingSummary()
      .then((s) => { if (alive) setSummary(s); })
      .catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listPayments({ status: statusFilter, limit: 100 })
      .then((r) => { if (alive) setPayments(r.results); })
      .catch((e: Error) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [statusFilter]);

  return (
    <div>
      <PageHeader
        eyebrow="Биллинг"
        title="Подписки и платежи"
        description="Сводка по списаниям ЮKassa, ручные возвраты и управление инвойсами."
      />

      <div className="page-pad">
        {error && (
          <div className="mb-6 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-px bg-line border border-line lg:grid-cols-4">
          <Kpi label="Всего принято" value={summary ? formatKop(summary.total) : '—'} />
          <Kpi label="Активных подписок" value={summary ? String(summary.activeSubscriptions) : '—'} />
          <Kpi label="Неудачных списаний" value={summary ? String(summary.failed) : '—'} accent="red" />
          <Kpi label="Возвратов" value={summary ? String(summary.refunded) : '—'} />
        </div>

        <div className="mt-6 flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              className={
                'border px-3 py-1.5 text-[12px] font-medium transition ' +
                (statusFilter === f.value
                  ? 'border-ink bg-ink text-white'
                  : 'border-line bg-white text-ink/70 hover:border-ink/50')
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="card mt-4">
          <div className="border-b border-line px-4 py-4 md:px-5">
            <h2 className="text-[14px] font-medium">Платежи</h2>
          </div>

          {/* Desktop: таблица */}
          <div className="hidden md:block">
            <table className="table-grid">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Арендатор</th>
                  <th>Метод</th>
                  <th>Статус</th>
                  <th className="text-right">Сумма</th>
                  <th className="text-right">Дата</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td className="font-mono text-[12px]">{p.id.slice(0, 8)}</td>
                    <td>
                      <Link to={`/tenants/${p.tenantId}`} className="font-medium hover:underline">
                        {p.tenantName}
                      </Link>
                    </td>
                    <td className="text-ink/70">{p.method ?? '—'}</td>
                    <td>
                      <span className={badgeClass(p.status)}>{statusLabel(p.status)}</span>
                    </td>
                    <td className="text-right font-mono">{formatKop(p.amount)}</td>
                    <td className="text-right font-mono text-[12px] text-ink/60">
                      {formatDateTime(p.createdAt)}
                    </td>
                  </tr>
                ))}
                {!loading && payments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-ink/50">
                      Платежей пока нет
                    </td>
                  </tr>
                )}
                {loading && payments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-ink/40">
                      Загрузка…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile: карточки */}
          <ul className="divide-y divide-line md:hidden">
            {payments.map((p) => (
              <li key={p.id} className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/tenants/${p.tenantId}`}
                      className="block truncate text-[14px] font-medium text-ink hover:underline"
                    >
                      {p.tenantName}
                    </Link>
                    <div className="mt-0.5 font-mono text-[11px] text-ink/50">
                      {p.id.slice(0, 8)} · {p.method ?? '—'}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-ink/50">
                      {formatDateTime(p.createdAt)}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-[14px] font-medium">{formatKop(p.amount)}</div>
                    <span className={`${badgeClass(p.status)} mt-1`}>{statusLabel(p.status)}</span>
                  </div>
                </div>
              </li>
            ))}
            {!loading && payments.length === 0 && (
              <li className="px-4 py-8 text-center text-[13px] text-ink/50">Платежей пока нет</li>
            )}
            {loading && payments.length === 0 && (
              <li className="px-4 py-8 text-center text-[13px] text-ink/40">Загрузка…</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function badgeClass(status: PaymentStatusUpper): string {
  switch (status) {
    case 'SUCCEEDED': return 'badge-green';
    case 'FAILED':    return 'badge-red';
    case 'REFUNDED':  return 'badge-ink';
    default:          return 'badge-amber';
  }
}

function statusLabel(status: PaymentStatusUpper): string {
  switch (status) {
    case 'SUCCEEDED': return 'успешно';
    case 'FAILED':    return 'ошибка';
    case 'REFUNDED':  return 'возврат';
    case 'PENDING':   return 'в ожидании';
  }
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: 'red' }) {
  return (
    <div className="bg-white p-6">
      <div className="kpi-label">{label}</div>
      <div className={'mt-4 kpi-value ' + (accent === 'red' ? 'text-red-600' : '')}>{value}</div>
    </div>
  );
}
